import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { setHoveredColumn, trackHoveredColumn } from 'components/spreadsheet/hooks/useHoveredColumn'
import ReactDOM from 'react-dom'
import { useBlocker, useBeforeUnload, useSearchParams } from 'react-router-dom'
import Button from 'components/button'
import Toolbar from 'components/spreadsheet/components/toolbar'
import { FloatingMenuScope } from 'components/menu/floating'
import { openViewSettings } from 'components/spreadsheet/helpers/view-settings-bus'
import { IColumnTypes, IViewPortColumn, sortDirections } from 'components/spreadsheet/types'
import { directionOptions } from 'components/spreadsheet/components/menu/views/sort/directions'
import { Colour, Filter, Group, Lock } from 'components/icons'
import { computeAggregateValues } from 'components/spreadsheet/helpers/aggregate'
import { computeColumnBlockDropRects, resolveDraggedColumnBlock } from 'components/spreadsheet/helpers/columnOrder'
import { COLUMN_MIN_WIDTH, HEADER_HEIGHT, STATS_HEIGHT, ROW_NUMBER_COLUMN_WIDTH } from 'style/constants'

import Header, { HeaderIndicator } from 'components/spreadsheet/components/header'
import { describeColumnFilters } from 'components/spreadsheet/helpers/filterDescription'
import Aggregate from 'components/spreadsheet/components/aggregate'
import HeaderAddColumn from 'components/spreadsheet/components/header/add-column'
import Rows from 'components/spreadsheet/components/rows'
import VerticalScrollbar from 'components/spreadsheet/components/vertical-scrollbar'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { useViewportContext } from 'components/spreadsheet/contexts/data/useViewportContext'
import { DataContextProvider } from 'components/spreadsheet/contexts/data'
import ColumnMenu from 'components/spreadsheet/components/menu/views/column'
import BulkColumnMenu from 'components/spreadsheet/components/menu/views/column/bulk'
import AggregateMenu from 'components/spreadsheet/components/menu/views/aggregate'
import CellMenu from 'components/spreadsheet/components/menu/views/cell'
import Form from 'components/spreadsheet/components/form'
import Chart from 'components/spreadsheet/components/chart'
import Calendar from 'components/spreadsheet/components/calendar'
import CustomView from 'components/spreadsheet/components/custom-view'
import { INITIAL_SELECTED_CELL, INITIAL_SELECTED_CELL_RANGE, ViewTypes } from 'components/spreadsheet/constants/const'
import { getViewCapabilities, hasColumnMenuOptions } from 'components/spreadsheet/constants/viewCapabilities'
import ErrorBoundary from 'components/error'
import GroupContextMenu from './components/grouping/GroupContextMenu'
import { IContextMenuState, IProcessObject, ITableViewColumn, ITableViewFilter, ITableViewSort } from 'types'
import NoPermission from 'views/auth/views/nopermission'
import ResourceDeleted from 'views/auth/views/resourceDeleted'
import ExpandedRow from 'components/spreadsheet/components/modal/views/expanded'
import { useProject } from 'hooks/useProject'
import DeleteColumnModal from 'components/spreadsheet/components/modal/views/deletecolumn'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import ArchivedWarning from 'components/archived'
import { getAllGroupRows } from 'components/spreadsheet/helpers/grouping'
import DeleteRowModal from 'components/spreadsheet/components/modal/views/deleterow'
import AuditLogModal from 'components/spreadsheet/components/modal/views/audit'
import EditColumnMenu from 'components/spreadsheet/components/menu/views/editcolumn'
import Spinner from 'components/spinner'
import ProgressBar from 'components/progressBar'
import MortaAgent from 'components/agent'
import BusyOverlay from 'components/spreadsheet/components/busy-overlay'
import SyncStatus from 'components/spreadsheet/components/sync-status'
import { GridSkeleton } from 'components/spreadsheet/components/loadingSkeleton'

interface SpreadsheetProps {
  tableId: string
  tableViewId: string
  permissionCap?: number
  processId?: string
  processSectionId?: string
  process?: IProcessObject
  showToolbar?: boolean
  showAggregate?: boolean
  fitToHeight?: boolean
}

const Spreadsheet: React.FC<SpreadsheetProps> = ({
  tableId,
  tableViewId,
  permissionCap,
  processId,
  processSectionId,
  process,
  showToolbar,
  showAggregate,
  fitToHeight
}) => {
  if (!tableId || !tableViewId)
    return (
      <div className="w-full h-full bg-surface-sunken flex text-red z-[2]">
        <span className="p-5">
          <b className="text-[110%]">⚠️ Error:</b> Could not load table - please remove and re-add
        </span>
      </div>
    )

  return (
    <DataContextProvider
      tableId={tableId}
      tableViewId={tableViewId}
      permissionCap={permissionCap}
      processId={processId}
      processSectionId={processSectionId}
      process={process}
      showToolbar={showToolbar}
      showAggregate={showAggregate}
      fitToHeight={fitToHeight}
    >
      <SpreadsheetComponent />
    </DataContextProvider>
  )
}

interface IExpandedRow {
  rowId: string
  rowNumber: number
}

const SpreadsheetComponent: React.FC = () => {
  const { handleScroll, attachGrid, rowsAreaHeight, columns, treeColumnWidth, treeLeafColumn } = useViewportContext()
  const {
    spreadsheetData,
    setSpreadsheetData,
    uniqueNumber,
    allColumns,
    totalWidth,
    handleToggleCollapseAll,
    handleCreateRow,
    handleCreateBlankRows,
    setCellValue,
    selectedCell,
    setSelectedCell,
    selectedCellRange,
    setSelectedCellRange,
    selectedColumnIds,
    clearColumnSelection,
    selectedRowIds,
    clearRowSelection,
    getRowIdsToDelete,
    groupedRows,
    showToolbar,
    showAggregate,
    fitToHeight,
    inProcess,
    updateGridDimensions
  } = useDataContext()

  const [searchParams, setSearchParams] = useSearchParams()
  const [showDeleteColumnModal, setShowDeleteColumnModal] = useState<boolean>(false)
  const [deleteColumnIds, setDeleteColumnIds] = useState<string[]>([])
  const [editCellMenu, setEditCellMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [editColumnMenu, setEditColumnMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [changeColumnMenu, setChangeColumnMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [editAggregateMenu, setEditAggregateMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [groupMenu, setGroupMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [dragOver, setDragOver] = useState<string>('')
  const [fullScreen, setFullScreen] = useState<boolean>(false)
  const inlineContainerRef = useRef<HTMLDivElement>(null)
  // Stable portal target — never changes identity so React never remounts the children.
  // Moved between the inline container and #root when fullscreen toggles.
  const [portalTarget] = useState(() => {
    const el = document.createElement('div')
    el.style.display = 'contents'
    return el
  })
  const [expandModal, updateExpandModal] = useState<boolean>(false)
  const [draggedColumnId, setDraggedColumnId] = useState<string>('')
  const { project } = useProject()

  const [expandedRow, setExpandedRow] = useState<IExpandedRow>()
  const [deleteRowModal, setDeleteRowModal] = useState<boolean>(false)
  const [deleteRowIds, setDeleteRowIds] = useState<string[]>([])
  const [rowAuditModal, setRowAuditModal] = useState<{
    open: boolean
    rowId: string
  }>({ open: false, rowId: '' })

  const [focusInput, setFocusInput] = useState<boolean>(false)

  // The spreadsheet is a column: the toolbar and any sync banner take their own
  // height, and the data view (or its loading and empty states) fills the rest.
  // min-h-0 lets it shrink below its content so it scrolls instead of growing.
  const dataViewHeightClass = 'flex-1 min-h-0'
  // Why a transposed or matrix view shows only part of the table, if it does.
  const viewTruncations = spreadsheetData.viewContext?.truncations ?? []
  // Fit to height only applies to chart and calendar views — grids and forms keep their own scrolling.
  const fitOverflowClass = fitToHeight ? 'overflow-hidden' : 'overflow-y-auto overflow-x-hidden'

  // The row menu shows its plural, multi-cell variant whenever the selection covers more than one
  // cell. A whole-row selection always does (a row spans every column), including the
  // Ctrl/Cmd+click case that has no cell rectangle to read.
  const multiCellSelection =
    selectedRowIds.length > 0 || (selectedCellRange.endRowIndex !== -1 && selectedCellRange.endColumnIndex !== -1)

  // Right-clicking a column that's part of a 2+ column selection opens the bulk-action menu
  // instead of the single-column menu. (The header reconciles the selection before opening,
  // so a right-click outside the selection has already collapsed it to a single column.)
  const showBulkColumnMenu =
    editColumnMenu.open &&
    selectedColumnIds.length >= 2 &&
    editColumnMenu.columnId !== undefined &&
    selectedColumnIds.includes(editColumnMenu.columnId)

  const shouldBlock = spreadsheetData.userConfiguration.unsavedChanges && spreadsheetData.isAdmin
  const blocker = useBlocker(shouldBlock)

  useEffect(() => {
    if (blocker.state === 'blocked') {
      const shouldLeave = window.confirm(
        'There are unsaved changes - are you sure you wish to navigate away from this page?'
      )
      if (shouldLeave) {
        blocker.proceed()
      } else {
        blocker.reset()
      }
    }
  }, [blocker])

  // Handle browser refresh/close
  useBeforeUnload((event) => {
    if (shouldBlock) {
      event.preventDefault()
    }
  })

  // Move the stable portal target between inline container and #root.
  // appendChild implicitly moves a node that already has a parent — no remove needed.
  // useLayoutEffect so the move happens before paint, avoiding flicker.
  useLayoutEffect(() => {
    const parent = fullScreen ? document.getElementById('root') || document.body : inlineContainerRef.current
    if (parent && portalTarget.parentNode !== parent) {
      parent.appendChild(portalTarget)
    }
  }, [fullScreen, portalTarget])

  // Clean up portal target on unmount only
  useEffect(() => {
    return () => portalTarget.remove()
  }, [portalTarget])

  // Update grid dimensions when fullscreen mode changes
  // The ResizeObserver may not detect the size change immediately when the parent
  // container switches between relative and fixed positioning
  useEffect(() => {
    // Use requestAnimationFrame to ensure the layout has been updated
    const rafId = requestAnimationFrame(() => {
      updateGridDimensions()
    })
    return () => cancelAnimationFrame(rafId)
  }, [fullScreen, updateGridDimensions])

  const openRowAuditModal = (open: boolean, rowId: string) => {
    setRowAuditModal({ open, rowId })
  }

  const modifyExpandModalState = (expandModal: boolean, focusInput: boolean, rowId: string, rowNumber: number) => {
    updateExpandModal(expandModal)
    setFocusInput(focusInput)
    setExpandedRow({ rowId, rowNumber })
  }

  // Open expanded row modal if URL contains ?row= parameter
  useEffect(() => {
    if (spreadsheetData.loading || !getViewCapabilities(spreadsheetData.viewDetails.type).rows.expandRow) return

    const expandedRowId = searchParams.get('row')
    if (expandedRowId) {
      const bitmap = spreadsheetData.visibleBitmap
      let foundRowIndex = -1
      if (bitmap) {
        let visibleIdx = 0
        for (let i = 0; i < spreadsheetData.rows.length; i++) {
          if (bitmap[i]) {
            if (spreadsheetData.rows[i].publicId === expandedRowId) {
              foundRowIndex = visibleIdx
              break
            }
            visibleIdx++
          }
        }
      } else {
        foundRowIndex = spreadsheetData.rows.findIndex((row) => row.publicId === expandedRowId)
      }
      if (foundRowIndex !== -1) {
        modifyExpandModalState(true, false, expandedRowId, foundRowIndex)
      } else {
        setSearchParams(
          (params) => {
            params.delete('row')
            return params
          },
          { replace: true }
        )
      }
    }
  }, [spreadsheetData.loading, searchParams])

  const handleClick = (event: any) => {
    const element = document.getElementById(`spreadsheet-${uniqueNumber}`)
    const dataViewElement = document.getElementById(`data-view-${uniqueNumber}`)
    const selectCellElement = document.getElementById('select-cell-menu')
    const rowContextElement = document.getElementById(`row-context-menu-${uniqueNumber}`)
    const formCellMenuElement = document.getElementById(`form-cell-menu-${uniqueNumber}`)
    const expandedRowModal = document.getElementById('expanded-row-modal')
    const rowCommentsModal = document.getElementById('row-comments')
    const searchMenuElement = document.getElementById(`search-context-menu-${uniqueNumber}`)
    const deleteRowModal = document.getElementById('delete-row-modal')
    const deleteColumnModal = document.getElementById('delete-column-modal')
    const rowAuditModal = document.getElementById('row-audit-modal')

    // Helper to check if click target or any parent is inside a given element
    const isInsideElement = (target: any, container: HTMLElement | null): boolean => {
      if (!container) return false
      let current = target
      while (current) {
        if (current === container) return true
        current = current.parentElement
      }
      return false
    }

    // Clicking anywhere that isn't a column header (or the column menu / delete-column modal it
    // drives) clears the whole-column selection — including the blank space below or beside a grid
    // that doesn't fill the viewport, and clicks outside the spreadsheet entirely.
    const columnMenuElement = document.getElementById(`column-context-menu-${uniqueNumber}`)
    const clickedColumnHeader = !!event.target?.closest?.('[id^="column/"]')
    const preserveColumnSelection =
      clickedColumnHeader ||
      isInsideElement(event.target, columnMenuElement) ||
      isInsideElement(event.target, deleteColumnModal)
    if (selectedColumnIds.length > 0 && !preserveColumnSelection) {
      clearColumnSelection()
    }

    // Same rule on the row axis: only a row-number cell (or the row menu / delete-row confirmation
    // it drives) keeps a whole-row selection alive. Anything else — a data cell, a column header,
    // the blank space around the grid, a click outside the spreadsheet — clears it.
    const clickedRowNumberCell = !!event.target?.closest?.('[id^="numbercell/"]')
    const preserveRowSelection =
      clickedRowNumberCell ||
      isInsideElement(event.target, rowContextElement) ||
      isInsideElement(event.target, deleteRowModal)
    if (selectedRowIds.length > 0 && !preserveRowSelection) {
      clearRowSelection()
    }

    const isOnBlankSpace = (target: any): boolean => {
      const rowFooterContainer = document.getElementById(`data-view-${uniqueNumber}`)

      if (!isInsideElement(target, rowFooterContainer)) return false

      // Spreadsheet view: specific blank space elements
      if (
        target.id === 'row-footer-container' ||
        target.id === 'spreadsheet-row-cols' ||
        target.id === `data-view-${uniqueNumber}`
      ) {
        return true
      }

      // Form view: inside form-container but not inside a form-field-cell
      if (target.closest?.('.form-container') && !target.closest?.('.form-field-cell')) {
        return true
      }

      return false
    }

    const isOnScrollbar = (event: MouseEvent, container: HTMLElement): boolean => {
      const rect = container.getBoundingClientRect()
      const scrollbarThreshold = 20 // pixels from edge to consider as scrollbar area

      // Only the horizontal scrollbar is the browser's; the vertical one is the grid's own
      // element outside the scroll container, which this handler never reaches.
      const hasHorizontalScrollbar = container.scrollWidth > container.clientWidth

      // Check if click is near the bottom edge (horizontal scrollbar)
      const isNearBottomEdge = event.clientY > rect.bottom - scrollbarThreshold

      return hasHorizontalScrollbar && isNearBottomEdge
    }

    if (
      (!rowCommentsModal || (rowCommentsModal && !isInsideElement(event.target, rowCommentsModal))) &&
      ((element && isInsideElement(event.target, element)) ||
        event.target.id === 'calendar-today-icon' ||
        event.target.id === 'calendar-today-icon-container' ||
        event.target.id === 'select-value-list' ||
        isInsideElement(event.target, selectCellElement) ||
        isInsideElement(event.target, rowContextElement) ||
        isInsideElement(event.target, formCellMenuElement) ||
        isInsideElement(event.target, expandedRowModal) ||
        isInsideElement(event.target, searchMenuElement) ||
        isInsideElement(event.target, deleteRowModal) ||
        isInsideElement(event.target, deleteColumnModal) ||
        isInsideElement(event.target, rowAuditModal) ||
        event.target.classList.contains('option-text') ||
        event.target.parentElement?.id?.includes(`row-context-menu-${uniqueNumber}`))
    ) {
      // Clicked inside spreadsheet
      if (isOnBlankSpace(event.target) && dataViewElement && !isOnScrollbar(event, dataViewElement)) {
        setSelectedCell(INITIAL_SELECTED_CELL)
        setSelectedCellRange(INITIAL_SELECTED_CELL_RANGE)
      }
    } else {
      // Click outside of spreadsheet
      setSelectedCell(INITIAL_SELECTED_CELL)
      setSelectedCellRange(INITIAL_SELECTED_CELL_RANGE)
    }
  }

  // Use ref to hold the latest handler - prevents memory leaks from stale closures
  const handleClickRef = useRef(handleClick)
  useEffect(() => {
    handleClickRef.current = handleClick
  })

  useEffect(() => {
    const onClick = (e: MouseEvent) => handleClickRef.current(e)
    document.addEventListener('click', onClick)
    return () => {
      document.removeEventListener('click', onClick)
    }
  }, []) // Empty deps - listener registered once

  const handleOnCloseExpandModal = () => {
    setSelectedCell(INITIAL_SELECTED_CELL)
    setSelectedCellRange(INITIAL_SELECTED_CELL_RANGE)
    setSearchParams(
      (params) => {
        params.delete('row')
        return params
      },
      { replace: true }
    )
  }

  // Use totalWidth from column virtualization, adding width for the add-column button if admin
  const gridWidth = totalWidth + (spreadsheetData.isAdmin ? COLUMN_MIN_WIDTH : 0)

  const cumulativeWidth = 0

  // Compute the size of the ghost based on the viewport
  const dropGhostRects = draggedColumnId
    ? computeColumnBlockDropRects(
        allColumns.map((viewportColumn) => ({
          publicId: viewportColumn.column.publicId,
          width: viewportColumn.column.width
        })),
        resolveDraggedColumnBlock(
          draggedColumnId,
          selectedColumnIds,
          spreadsheetData.userConfiguration.groupSettings.map((setting) => setting.columnId)
        ),
        dragOver,
        ROW_NUMBER_COLUMN_WIDTH + treeColumnWidth
      )
    : []

  // When bitmap filtering is active and not grouping, only aggregate over visible rows
  let aggregateRows: typeof spreadsheetData.rows
  if (groupedRows) {
    aggregateRows = getAllGroupRows(groupedRows) as typeof spreadsheetData.rows
  } else if (spreadsheetData.visibleBitmap) {
    const { rows, visibleBitmap } = spreadsheetData
    const result: typeof spreadsheetData.rows = []
    for (let i = 0; i < rows.length; i++) {
      if (visibleBitmap[i]) result.push(rows[i])
    }
    aggregateRows = result
  } else {
    aggregateRows = spreadsheetData.rows
  }
  const aggregateColumns = treeLeafColumn ? [treeLeafColumn, ...columns] : columns
  const computedAggregates = computeAggregateValues(aggregateRows, aggregateColumns, spreadsheetData)

  // What acts on each column, as icons on its header, each named on hover: built once per render,
  // not looked up per header, most important first, as the last give way to a long name.
  const config = spreadsheetData.userConfiguration
  const countBy = (ids: string[]) => ids.reduce((m, id) => m.set(id, (m.get(id) ?? 0) + 1), new Map<string, number>())
  const sortByColumn = new Map(config.sortSettings.map((s: ITableViewSort) => [s.columnId, s]))
  const groupByColumn = new Map(config.groupSettings.map((g) => [g.columnId, g]))
  const filtersByColumn = config.filterSettings.reduce(
    (byColumn, filter: ITableViewFilter) =>
      byColumn.set(filter.columnId, [...(byColumn.get(filter.columnId) ?? []), filter]),
    new Map<string, ITableViewFilter[]>()
  )
  const colourCounts = countBy(config.colourSettings.map((c) => c.columnId))
  const lockCounts = countBy(config.rowLockSettings.map((l) => l.columnId))
  const directionLabel = (kind: IColumnTypes, direction: sortDirections) =>
    directionOptions(kind).find((option) => option.value === direction) ?? directionOptions(kind)[0]
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`
  // A direction's name mid-sentence: "oldest first", but "A to Z" as it is.
  const inSentence = (label: string) => label.replace(/^[A-Z](?=[a-z])/, (letter) => letter.toLowerCase())
  const columnIndicators = (column: ITableViewColumn): HeaderIndicator[] => {
    const indicators: HeaderIndicator[] = []
    const group = groupByColumn.get(column.publicId)
    if (group) {
      indicators.push({
        key: 'group',
        icon: <Group />,
        label: `Grouped, ${inSentence(directionLabel(column.kind, group.direction).label)}`
      })
    }
    const filters = filtersByColumn.get(column.publicId)
    if (filters) {
      indicators.push({ key: 'filter', icon: <Filter />, label: describeColumnFilters(filters, column.kind) })
    }
    const sort = sortByColumn.get(column.publicId)
    if (sort) {
      const direction = directionLabel(column.kind, sort.direction)
      indicators.push({ key: 'sort', icon: direction.icon, label: `Sorted ${inSentence(direction.label)}` })
    }
    const colours = colourCounts.get(column.publicId) ?? 0
    if (colours > 0) {
      indicators.push({ key: 'colour', icon: <Colour />, label: `${plural(colours, 'conditional formatting rule')}` })
    }
    const locks = lockCounts.get(column.publicId) ?? 0
    if (locks > 0) {
      indicators.push({ key: 'lock', icon: <Lock />, label: `Cells locked by ${plural(locks, 'rule')}` })
    }
    return indicators
  }

  const showColumnMenu = hasColumnMenuOptions(spreadsheetData.viewDetails.type)

  // Skip ResourceDeleted check if in template render mode
  const isTemplateRender = spreadsheetData.templateRender === true

  if (
    !inProcess &&
    !isTemplateRender &&
    !spreadsheetData.loading &&
    spreadsheetData.tableDetails.projectPublicId !== '' &&
    project.publicId !== '' &&
    spreadsheetData.tableDetails.projectPublicId !== project.publicId
  ) {
    return <ResourceDeleted inProcess={inProcess} />
  }

  if (spreadsheetData.errorStatusCode === 403) {
    return (
      <NoPermission
        inProcess={inProcess}
        type={'view'}
        id={spreadsheetData.activeTableView}
        project={project.publicId}
      />
    )
  } else if (spreadsheetData.errorStatusCode === 404) {
    return <ResourceDeleted inProcess={inProcess} />
  } else {
    // Cells are 13px, the size of the header's names (which are set apart by
    // weight instead): Inter at 14px read larger than the header above it. At
    // 13px, Inter's regular (400) reads thin; 450 (from the variable font) sits
    // between it and the header's 600.
    // What the loading grid reports on top of its placeholders: nothing until
    // the first rows arrive, then the stream's progress, then the filter pass.
    const loadingStatus = (): React.ReactNode => {
      if (spreadsheetData.rowStreamComplete) {
        return (
          <>
            <Spinner size="small" />
            <span className="text-muted text-[12px]">Applying filters...</span>
          </>
        )
      }
      if (spreadsheetData.loadingProgress === 0) return null
      return (
        <>
          <ProgressBar
            percentage={spreadsheetData.loadingProgress}
            classNames={{
              track: 'h-[6px] rounded-full bg-surface-strong',
              fill: 'h-[6px] rounded-full bg-blue transition-all duration-300 ease-out'
            }}
          />
          <span className="text-muted text-[12px]">Loading rows... {spreadsheetData.loadingProgress}%</span>
        </>
      )
    }

    const spreadsheetContent = (
      <div
        id={`spreadsheet-${uniqueNumber}`}
        data-testid="spreadsheet"
        className={`h-full w-full text-body text-[13px]/5 font-[450] bg-surface flex flex-col select-none overflow-visible border-inherit transform-none ${
          fullScreen ? 'fixed inset-0 z-[5]' : 'relative z-[1]'
        }`}
      >
        {spreadsheetData.tableDetails.isDeleted && <ArchivedWarning message={'This resource has been archived'} />}

        {!inProcess && <MortaAgent />}

        <BusyOverlay active={spreadsheetData.refreshing} label={spreadsheetData.busyLabel} />

        {showToolbar && (
          <ErrorBoundary>
            <Toolbar
              uniqueNumber={uniqueNumber}
              fullScreen={fullScreen}
              setFullScreen={setFullScreen}
              handleToggleCollapseAll={handleToggleCollapseAll}
              columns={allColumns}
            />
          </ErrorBoundary>
        )}

        <SyncStatus />

        {!spreadsheetData.loading && viewTruncations.length > 0 && (
          <div
            data-testid="view-truncation-notice"
            role="status"
            className="shrink-0 flex flex-col gap-0.5 px-3 py-1.5 text-[12px] text-body-soft bg-surface-sunken border-b border-divider"
          >
            {viewTruncations.map((truncation) => (
              <span key={truncation.message}>{truncation.message}</span>
            ))}
          </div>
        )}

        {/* A zero-height anchor in the flow, so the bar sits on the grid's top edge rather than on
            the toolbar above it, and above the grid's sticky header (z-[7]). */}
        {!spreadsheetData.loading && !spreadsheetData.rowStreamComplete && (
          <div className="relative h-0">
            <div
              data-testid="spreadsheet-streaming"
              className="absolute left-0 right-0 top-0 z-[8] pointer-events-none"
              aria-label={`Loading rows... ${spreadsheetData.loadingProgress}%`}
            >
              <ProgressBar
                percentage={spreadsheetData.loadingProgress}
                classNames={{
                  track: 'h-[3px] bg-transparent',
                  fill: 'h-[3px] bg-blue transition-all duration-300 ease-out'
                }}
              />
            </div>
          </div>
        )}

        {spreadsheetData.loading && <GridSkeleton className={dataViewHeightClass}>{loadingStatus()}</GridSkeleton>}
        {!spreadsheetData.loading &&
          spreadsheetData.viewDetails.type === ViewTypes.TRANSPOSE &&
          !spreadsheetData.viewDetails.transposeSettings?.pivotColumnId && (
            <div
              className={`relative w-full flex flex-col items-center justify-center gap-3 p-8 text-center bg-surface ${dataViewHeightClass}`}
            >
              <div className="text-lg font-medium text-body-soft">Select a pivot column to get started</div>
              <Button internalType="primary" onClick={() => openViewSettings('table-pivot-button')}>
                Choose pivot column
              </Button>
            </div>
          )}
        {!spreadsheetData.loading &&
          spreadsheetData.viewDetails.type === ViewTypes.MATRIX &&
          (!spreadsheetData.viewDetails.matrixSettings?.pivotColumnId ||
            (spreadsheetData.viewDetails.matrixSettings?.rowLabelColumnIds.length ?? 0) === 0 ||
            (spreadsheetData.viewDetails.matrixSettings?.dataFieldColumnIds.length ?? 0) === 0) && (
            <div
              className={`relative w-full flex flex-col items-center justify-center gap-3 p-8 text-center bg-surface ${dataViewHeightClass}`}
            >
              <div className="text-lg font-medium text-body-soft">
                Configure row labels, a column label, and data fields to build your matrix.
              </div>
              <Button internalType="primary" onClick={() => openViewSettings('table-matrix-button')}>
                Configure matrix view
              </Button>
            </div>
          )}
        {gridWidth > 0 &&
          !spreadsheetData.loading &&
          (spreadsheetData.viewDetails.type === ViewTypes.SPREADSHEET ||
            (spreadsheetData.viewDetails.type === ViewTypes.TRANSPOSE &&
              spreadsheetData.viewDetails.transposeSettings?.pivotColumnId) ||
            (spreadsheetData.viewDetails.type === ViewTypes.MATRIX &&
              spreadsheetData.viewDetails.matrixSettings?.pivotColumnId &&
              (spreadsheetData.viewDetails.matrixSettings?.rowLabelColumnIds.length ?? 0) > 0 &&
              (spreadsheetData.viewDetails.matrixSettings?.dataFieldColumnIds.length ?? 0) > 0)) && (
            <ErrorBoundary>
              <div
                id={`data-view-${uniqueNumber}`}
                data-testid="data-view-spreadsheet"
                // Scrolls horizontally only: vertical scrolling is virtual (helpers/scroll.ts).
                className={`relative bg-surface-sunken w-full overflow-x-scroll overflow-y-hidden ${!showToolbar ? 'border-t border-divider' : ''} ${dataViewHeightClass}`}
                ref={attachGrid}
                onScroll={handleScroll}
                // Which column the pointer (or focus) is in, so its header can show its ⋮.
                onMouseOver={(event) => trackHoveredColumn(uniqueNumber, event)}
                onFocus={(event) => trackHoveredColumn(uniqueNumber, event)}
                onMouseLeave={() => setHoveredColumn(null)}
              >
                <div
                  className="sticky bg-surface-sunken border-b border-divider z-[7] top-[-1px]"
                  style={{ width: gridWidth, height: `${HEADER_HEIGHT}px` }}
                >
                  {/* The corner above the row numbers, in the hub's header colours (Hub settings › Styling). */}
                  <Header
                    width={ROW_NUMBER_COLUMN_WIDTH}
                    cumulativeWidth={cumulativeWidth}
                    frozen={true}
                    isLastFrozenColumn={false}
                    backgroundColor={project.defaultHeaderBackgroundColor}
                    textColor={project.defaultHeaderTextColor}
                  />
                  {treeColumnWidth > 0 && treeLeafColumn && (
                    <Header
                      columnNumber={treeLeafColumn.originalPosition}
                      width={treeColumnWidth}
                      cumulativeWidth={ROW_NUMBER_COLUMN_WIDTH}
                      value={treeLeafColumn.column.name}
                      kind={treeLeafColumn.column.kind}
                      columnId={treeLeafColumn.column.publicId}
                      optionClick={showColumnMenu ? setEditColumnMenu : undefined}
                      frozen={true}
                      isAdmin={spreadsheetData.isAdmin}
                      isLastFrozenColumn={false}
                      viewId={spreadsheetData.viewDetails.publicId}
                      textColor={treeLeafColumn.column.headerTextColor}
                      backgroundColor={treeLeafColumn.column.headerBackgroundColor}
                      description={treeLeafColumn.column.description}
                      indicators={columnIndicators(treeLeafColumn.column)}
                    />
                  )}
                  {allColumns.map((viewportColumn: IViewPortColumn) => {
                    return (
                      <Header
                        key={viewportColumn.column.publicId}
                        columnNumber={viewportColumn.originalPosition}
                        width={viewportColumn.column.width}
                        cumulativeWidth={viewportColumn.left}
                        value={viewportColumn.column.name}
                        locked={viewportColumn.isGrouped}
                        kind={viewportColumn.column.kind}
                        optionClick={showColumnMenu ? setEditColumnMenu : undefined}
                        frozen={viewportColumn.isFrozen}
                        indicators={columnIndicators(viewportColumn.column)}
                        dragOver={dragOver}
                        setDragOver={setDragOver}
                        isAdmin={spreadsheetData.isAdmin}
                        isSynced={viewportColumn.column.isSynced}
                        isLastFrozenColumn={viewportColumn.isLastFrozenColumn}
                        columnId={viewportColumn.column.publicId}
                        viewId={spreadsheetData.viewDetails.publicId}
                        draggedColumnId={draggedColumnId}
                        setDraggedColumnId={setDraggedColumnId}
                        textColor={viewportColumn.column.headerTextColor}
                        backgroundColor={viewportColumn.column.headerBackgroundColor}
                        description={viewportColumn.column.description}
                      />
                    )
                  })}
                  {spreadsheetData.isAdmin && getViewCapabilities(spreadsheetData.viewDetails.type).rows.addColumn && (
                    <HeaderAddColumn
                      spreadsheetData={spreadsheetData}
                      setSpreadsheetData={setSpreadsheetData}
                      totalWidth={totalWidth}
                    />
                  )}
                </div>

                {dropGhostRects.map((rect) => (
                  <div
                    key={rect.left}
                    data-testid="column-drop-ghost"
                    className="absolute top-0 pointer-events-none z-[8] bg-black/25"
                    style={{
                      left: `${rect.left}px`,
                      width: `${rect.width}px`,
                      height: `${HEADER_HEIGHT + rowsAreaHeight}px`
                    }}
                  />
                ))}

                {/* No minimum height: the footer follows straight after the
                    table's last row, and its sticky bottom-0 only pins it to
                    the bottom once the table is taller than the view. */}
                <div id="row-footer-container">
                  {/* The rows layer is absolutely positioned at the top of the grid and moved by
                      a transform; the block below it gives the rows area its height. */}
                  <Rows
                    contextClick={setEditCellMenu}
                    width={gridWidth}
                    onGroupContext={setGroupMenu}
                    setExpandModal={modifyExpandModalState}
                  />
                  {/* Behind the rows: white under the columns, the new-column
                      lane's grey under the +, as the rows paint them. */}
                  <div
                    id="spreadsheet-row-cols"
                    className="bg-surface"
                    style={{
                      height: rowsAreaHeight,
                      width: gridWidth,
                      backgroundImage: spreadsheetData.isAdmin
                        ? `linear-gradient(to left, var(--color-surface-sunken) ${COLUMN_MIN_WIDTH}px, transparent ${COLUMN_MIN_WIDTH}px)`
                        : undefined
                    }}
                  />
                </div>

                {showAggregate && getViewCapabilities(spreadsheetData.viewDetails.type).rows.aggregate && (
                  // Under a short table the footer is the table's last row, so it
                  // closes with a line; a shadow rather than a border, which would
                  // change the height the scroll maths expects. It stops at the last
                  // column, so its lines don't carry on under the new-column +.
                  <div
                    id="footer"
                    className="bg-surface sticky border-divider bottom-0 z-[7] border-t shadow-[0_1px_0_var(--color-divider)]"
                    style={{ width: totalWidth, height: `${STATS_HEIGHT}px` }}
                  >
                    <Aggregate
                      cumulativeWidth={cumulativeWidth}
                      width={ROW_NUMBER_COLUMN_WIDTH}
                      frozen={true}
                      isLastFrozenColumn={false}
                      className="border-t-0 bg-surface"
                      height={STATS_HEIGHT - 1}
                      loading={spreadsheetData.loading}
                    />
                    {treeColumnWidth > 0 && treeLeafColumn && (
                      <Aggregate
                        cumulativeWidth={ROW_NUMBER_COLUMN_WIDTH}
                        width={treeColumnWidth}
                        frozen={true}
                        isLastFrozenColumn={false}
                        column={treeLeafColumn}
                        computedValue={computedAggregates[treeLeafColumn.column.publicId]}
                        optionClick={setEditAggregateMenu}
                        columnNumber={0}
                        className="border-t-0 bg-surface"
                        height={STATS_HEIGHT - 1}
                        loading={spreadsheetData.loading}
                      />
                    )}
                    {columns.map((viewportColumn, columnNumber) => {
                      const adjustedColumnNumber = treeLeafColumn ? columnNumber + 1 : columnNumber
                      return (
                        <Aggregate
                          key={viewportColumn.column.publicId}
                          cumulativeWidth={viewportColumn.left}
                          width={viewportColumn.column.width}
                          frozen={viewportColumn.isFrozen}
                          isLastFrozenColumn={viewportColumn.isLastFrozenColumn}
                          computedValue={computedAggregates[viewportColumn.column.publicId]}
                          optionClick={setEditAggregateMenu}
                          column={viewportColumn}
                          columnNumber={adjustedColumnNumber}
                          className="border-t-0 bg-surface"
                          height={STATS_HEIGHT - 1}
                          loading={spreadsheetData.loading}
                        />
                      )
                    })}
                  </div>
                )}

                {showBulkColumnMenu ? (
                  <BulkColumnMenu
                    id={`column-context-menu-${uniqueNumber}`}
                    menuState={editColumnMenu}
                    setMenuState={setEditColumnMenu}
                    width={250}
                    openDeleteColumnsModal={(columnIds: string[]) => {
                      setDeleteColumnIds(columnIds)
                      setShowDeleteColumnModal(true)
                    }}
                  />
                ) : (
                  <ColumnMenu
                    id={`column-context-menu-${uniqueNumber}`}
                    menuState={editColumnMenu}
                    setMenuState={setEditColumnMenu}
                    width={250}
                    setEditColumnMenu={(open: boolean, panel?: 'formula') => {
                      setEditCellMenu(INITIAL_CONTEXT_MENU_STATE)
                      setEditColumnMenu(INITIAL_CONTEXT_MENU_STATE)
                      setChangeColumnMenu({
                        open,
                        right: editColumnMenu.right,
                        top: editColumnMenu.top,
                        bottom: editColumnMenu.bottom,
                        left: editColumnMenu.left,
                        columnNumber: editColumnMenu.columnNumber,
                        columnId: editColumnMenu.columnId,
                        columnPanel: panel
                      })
                    }}
                    setDeleteColumnModal={(open: boolean) => {
                      if (open) setDeleteColumnIds(editColumnMenu.columnId ? [editColumnMenu.columnId] : [])
                      setShowDeleteColumnModal(open)
                    }}
                  />
                )}
                {changeColumnMenu.open && (
                  <EditColumnMenu
                    id={`edit-column-menu-${uniqueNumber}`}
                    menuState={changeColumnMenu}
                    setMenuState={setChangeColumnMenu}
                  />
                )}

                {editCellMenu.open && (
                  <CellMenu
                    id={`row-context-menu-${uniqueNumber}`}
                    menuState={editCellMenu}
                    setMenuState={setEditCellMenu}
                    width={250}
                    onCreateRow={handleCreateRow}
                    onCreateBlankRows={handleCreateBlankRows}
                    setExpandModal={modifyExpandModalState}
                    setRowAuditModal={openRowAuditModal}
                    multiCellSelection={multiCellSelection}
                    setDeleteRowModal={(open: boolean) => {
                      // Resolve the rows the cell selection spans now, while it is still intact —
                      // the confirmation then counts them like any other bulk row delete.
                      if (open) setDeleteRowIds(getRowIdsToDelete())
                      setDeleteRowModal(open)
                    }}
                  />
                )}

                {showDeleteColumnModal && (
                  <DeleteColumnModal
                    id="delete-column-modal"
                    open={showDeleteColumnModal}
                    setOpen={setShowDeleteColumnModal}
                    columnIds={deleteColumnIds}
                  />
                )}

                {rowAuditModal.open && rowAuditModal.rowId && rowAuditModal.rowId !== '' && (
                  <AuditLogModal
                    id="row-audit-modal"
                    open={rowAuditModal.open}
                    setOpen={(open: boolean) => openRowAuditModal(open, '')}
                    rowId={rowAuditModal.rowId}
                    verbs={['table.cell_updated']}
                  />
                )}

                {deleteRowModal && (
                  <DeleteRowModal
                    id="delete-row-modal"
                    open={deleteRowModal}
                    setOpen={(open: boolean) => {
                      setDeleteRowModal(open)
                      // Drop the resolved list on close so a later delete can't inherit it.
                      if (!open) setDeleteRowIds([])
                    }}
                    rowIds={deleteRowIds}
                  />
                )}

                {groupMenu.open && (
                  <GroupContextMenu
                    id={`grouping-context-menu-${uniqueNumber}`}
                    menuState={groupMenu}
                    setMenuState={setGroupMenu}
                    width={175}
                  />
                )}

                {editAggregateMenu.open && (
                  <AggregateMenu
                    id={`column-aggregate-menu-${uniqueNumber}`}
                    menuState={editAggregateMenu}
                    setMenuState={setEditAggregateMenu}
                    width={175}
                  />
                )}
              </div>
              <VerticalScrollbar />
            </ErrorBoundary>
          )}
        {!spreadsheetData.loading && spreadsheetData.viewDetails.type === ViewTypes.FORM && (
          <ErrorBoundary>
            <div
              id={`data-view-${uniqueNumber}`}
              data-testid="data-view-form"
              className={`relative bg-surface-sunken w-full overflow-y-auto overflow-x-hidden text-base ${dataViewHeightClass}`}
            >
              <Form />
            </div>
          </ErrorBoundary>
        )}
        {!spreadsheetData.loading && spreadsheetData.viewDetails.type === ViewTypes.CHART && (
          <ErrorBoundary>
            <div
              id={`data-view-${uniqueNumber}`}
              data-testid="data-view-chart"
              className={`relative bg-surface-sunken w-full ${fitOverflowClass} text-base ${dataViewHeightClass}`}
            >
              <Chart />
            </div>
          </ErrorBoundary>
        )}
        {!spreadsheetData.loading && spreadsheetData.viewDetails.type === ViewTypes.CALENDAR && (
          <ErrorBoundary>
            <div
              id={`data-view-${uniqueNumber}`}
              data-testid="data-view-calendar"
              className={`relative bg-surface w-full ${fitOverflowClass} text-base ${dataViewHeightClass}`}
            >
              <Calendar setExpandModal={modifyExpandModalState} />
            </div>
          </ErrorBoundary>
        )}

        {!spreadsheetData.loading && spreadsheetData.viewDetails.type === ViewTypes.CUSTOM_VIEW && (
          <ErrorBoundary>
            <div className={`relative bg-surface w-full overflow-auto ${dataViewHeightClass}`}>
              <CustomView key={spreadsheetData.viewDetails.publicId} />
            </div>
          </ErrorBoundary>
        )}

        {expandModal && getViewCapabilities(spreadsheetData.viewDetails.type).rows.expandRow && (
          <ExpandedRow
            id="expanded-row-modal"
            open={expandModal}
            setOpen={updateExpandModal}
            focusInput={focusInput}
            setCellValue={setCellValue}
            setSelectedCell={setSelectedCell}
            selectedCell={selectedCell}
            onClose={handleOnCloseExpandModal}
            initialRowId={expandedRow?.rowId}
            initialRowNumber={expandedRow?.rowNumber}
          />
        )}
      </div>
    )

    // Always render via a stable portal target. The target is moved between the
    // inline container (normal mode) and #root (fullscreen) by the effect above.
    // Because the portal target identity never changes, React preserves the entire
    // component tree — no remount, no lost form state.
    return (
      <>
        <div ref={inlineContainerRef} className={fullScreen ? 'hidden' : 'h-full w-full'} />
        {ReactDOM.createPortal(
          // A moved panel closes when the view changes, which in a document happens without navigating.
          <FloatingMenuScope value={`${spreadsheetData.tableDetails.publicId}|${spreadsheetData.viewDetails.publicId}`}>
            {spreadsheetContent}
          </FloatingMenuScope>,
          portalTarget
        )}
      </>
    )
  }
}

export default Spreadsheet
